import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAppCheckToken } from '../firebase/firebase-config';
import '../Styles/AskNaga.css';

const GREETING = {
  role: 'assistant',
  content: "Hi, I'm PP. How can I help?",
};

const LINK_PATTERN = /(Mentoring \(\/guidance\)|Contract Help \(\/contract-compass\)|Work With Me \(\/work-with-me\)|Podcast \(\/podcast\)|\/(?:blog|contract-tips)\/[a-z0-9-]+|\/guidance|\/work-with-me|\/podcast|\/contract-compass)/g;

const LINK_LABELS = {
  'Mentoring (/guidance)': 'Mentoring',
  'Contract Help (/contract-compass)': 'Contract Help',
  'Work With Me (/work-with-me)': 'Work With Me',
  'Podcast (/podcast)': 'Podcast',
  '/guidance': 'Mentoring',
  '/work-with-me': 'Work With Me',
  '/contract-compass': 'Contract Help',
  '/podcast': 'Podcast',
};

function renderMessage(text) {
  const parts = String(text || '').split(LINK_PATTERN);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <Link key={i} to={part.match(/\((\/[^)]+)\)$/)?.[1] || part} className="an-msg-link">{LINK_LABELS[part] || 'Read more'}</Link>
      : <span key={i}>{part}</span>
  );
}

export default function AskNaga() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setNudge(true), 8000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, open]);

  if (pathname.startsWith('/admin') || pathname.startsWith('/desk')) return null;

  const handleSendText = async (textToSend) => {
    const text = textToSend.trim();
    if (!text || loading) return;
    const history = [...messages, { role: 'user', content: text }];
    setMessages(history);
    setInput('');
    setLoading(true);
    setError('');
    try {
      const appCheckToken = await getAppCheckToken();
      const res = await fetch('/api/ai/visitor-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(appCheckToken ? { 'X-Firebase-AppCheck': appCheckToken } : {}),
        },
        body: JSON.stringify({ history: history.slice(-10) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Something went wrong.');
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError(err.message || "PP is offline for a moment — please try again.");
    }
    setLoading(false);
  };

  const send = (e) => {
    e.preventDefault();
    handleSendText(input);
  };

  const newConversation = () => {
    setMessages([GREETING]);
    setError('');
  };

  return (
    <div className={`an-root${pathname === '/blog' ? ' an-root--edit-cover' : ''}`}>
      {open && (
        <div className="an-panel" role="dialog" aria-label="Ask PP">
          <div className="an-panel-head">
            <div className="an-panel-head-text">
              <span className="an-panel-title">✦ Ask PP</span>
              <span className="an-panel-sub">AI assistant</span>
            </div>
            <div className="an-panel-head-actions">
              <button type="button" className="an-new" onClick={newConversation} aria-label="Start a new conversation" title="New conversation">↺</button>
              <button type="button" className="an-close" onClick={() => setOpen(false)} aria-label="Close chat">×</button>
            </div>
          </div>

          <div className="an-messages">
            {messages.map((m, i) => (
              <div key={i} className={`an-bubble ${m.role}`}>
                <span className="an-bubble-name">{m.role === 'assistant' ? 'PP' : 'You'}</span>
                <div className="an-bubble-text">{renderMessage(m.content)}</div>
              </div>
            ))}
            {loading && (
              <div className="an-bubble assistant">
                <span className="an-bubble-name">PP</span>
                <div className="an-typing"><span /><span /><span /></div>
              </div>
            )}
            {error && <div className="an-error">{error}</div>}
            <div ref={endRef} />
          </div>

          <p className="an-privacy-note">
            Please don't share confidential details, full contracts, or personal data here.
          </p>

          <form className="an-input-row" onSubmit={send}>
            <input
              type="text"
              className="an-input"
              placeholder="Ask about contracts, freelancing…"
              value={input}
              onChange={e => setInput(e.target.value)}
              disabled={loading}
              maxLength={500}
            />
            <button type="submit" className="an-send" disabled={loading || !input.trim()} aria-label="Send">→</button>
          </form>
        </div>
      )}

      <button
        type="button"
        className="an-fab"
        onClick={() => { setOpen(v => !v); setNudge(false); }}
        aria-label={open ? 'Close Ask PP' : 'Open Ask PP'}
      >
        {open ? '×' : '✦'}
      </button>
      {!open && nudge && (
        <button type="button" className="an-nudge" onClick={() => { setOpen(true); setNudge(false); }}>
          Contract question? Ask PP ✦
        </button>
      )}
    </div>
  );
}
